import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  QuizClientMessage,
  QuizServerError,
  QuizServerLobby,
  QuizServerPhase,
  QuizServerQuestion,
  QuizServerAnswered,
  QuizServerReveal,
  QuizServerLevelStats,
  QuizServerSuperOffer,
  QuizServerGameOver,
  QuizServerMessage,
} from '@shum/shared';
import { websocketUrl } from './runtime';
import { useConnectionWatchdog } from './connectionWatchdog';

export type QuizConnectionStatus = 'connecting' | 'open' | 'closed';

export interface QuizBattleClientState {
  status: QuizConnectionStatus;
  lobby: QuizServerLobby | null;
  phase: QuizServerPhase | null;
  question: QuizServerQuestion | null;
  answered: QuizServerAnswered | null;
  reveal: QuizServerReveal | null;
  levelStats: QuizServerLevelStats | null;
  superOffer: QuizServerSuperOffer | null;
  gameOver: QuizServerGameOver | null;
  error: QuizServerError | null;
  replaced: boolean;
  playerId: string | null;
}

const INITIAL: QuizBattleClientState = {
  status: 'connecting',
  lobby: null,
  phase: null,
  question: null,
  answered: null,
  reveal: null,
  levelStats: null,
  superOffer: null,
  gameOver: null,
  error: null,
  replaced: false,
  playerId: null,
};

function storedPlayerKey(code: string): string {
  return `quiz-battle-player:${code.toUpperCase()}`;
}

function readStoredPlayerId(code: string): string | null {
  try {
    return localStorage.getItem(storedPlayerKey(code));
  } catch {
    return null;
  }
}

function storePlayerId(code: string, playerId: string | null): void {
  try {
    if (playerId) localStorage.setItem(storedPlayerKey(code), playerId);
    else localStorage.removeItem(storedPlayerKey(code));
  } catch {
    /* приватный режим — держим только в памяти */
  }
}

export function useQuizBattleSocket(code: string | undefined) {
  const [state, setState] = useState<QuizBattleClientState>(INITIAL);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef<number>(0);
  const closedByUs = useRef(false);
  const connectRef = useRef<(() => void) | null>(null);
  const reconnectTimerRef = useRef<number | undefined>(undefined);
  const lastMessageAtRef = useRef<number>(Date.now());
  const playerIdRef = useRef<string | null>(null);
  const questionRef = useRef<QuizServerQuestion | null>(null);

  const send = useCallback((msg: QuizClientMessage) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    if (!code) return;
    closedByUs.current = false;
    playerIdRef.current = readStoredPlayerId(code);
    questionRef.current = null;
    setState(() => ({ ...INITIAL, playerId: playerIdRef.current }));

    let alive = true;

    const connect = () => {
      const current = wsRef.current;
      if (current && (current.readyState === WebSocket.CONNECTING || current.readyState === WebSocket.OPEN)) return;

      const url = websocketUrl(`/ws/quiz/${code}`);
      const ws = new WebSocket(url);
      wsRef.current = ws;
      const knownPlayerId = playerIdRef.current;
      lastMessageAtRef.current = Date.now();
      setState((s) => ({ ...s, status: 'connecting' }));

      ws.onopen = () => {
        if (!alive || wsRef.current !== ws) return;
        reconnectRef.current = 0;
        lastMessageAtRef.current = Date.now();
        if (knownPlayerId) {
          ws.send(JSON.stringify({ type: 'quiz.rejoin', playerId: knownPlayerId } satisfies QuizClientMessage));
        }
      };

      ws.onmessage = (event) => {
        if (!alive || wsRef.current !== ws) return;
        lastMessageAtRef.current = Date.now();
        let msg: QuizServerMessage;
        try {
          msg = JSON.parse(String(event.data)) as QuizServerMessage;
        } catch {
          return;
        }
        applyMessage(msg);
      };

      ws.onclose = () => {
        if (!alive || closedByUs.current || wsRef.current !== ws) return;
        wsRef.current = null;
        setState((s) => ({ ...s, status: 'closed' }));
        reconnectRef.current += 1;
        const delay = Math.min(8000, 800 * reconnectRef.current);
        if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = window.setTimeout(() => {
          reconnectTimerRef.current = undefined;
          if (alive && !closedByUs.current) connect();
        }, delay);
      };

      ws.onerror = () => {
        /* onclose will follow */
      };
    };

    const applyMessage = (msg: QuizServerMessage) => {
      setState((prev) => {
        switch (msg.type) {
          case 'hello':
            return { ...prev, status: playerIdRef.current ? prev.status : 'open' };
          case 'quiz.lobby':
            return { ...prev, lobby: msg, status: 'open' };
          case 'quiz.phase': {
            // Новый уровень сбрасывает результаты предыдущего экрана.
            const next: QuizBattleClientState = { ...prev, phase: msg, status: 'open' };
            if (msg.phase === 'LEVEL_INTRO' || msg.phase === 'LOBBY' || msg.phase === 'SUPER_OFFER') {
              next.question = null;
            }
            if (msg.phase !== 'REVEAL') next.reveal = null;
            if (msg.phase !== 'LEVEL_STATS') next.levelStats = null;
            if (msg.phase !== 'GAME_OVER') next.gameOver = null;
            if (msg.phase !== 'QUESTION' && msg.phase !== 'REVEAL') next.answered = null;
            // Предложение супер-игры живёт до конца игры (нужно в вопросе и reveal).
            if (msg.phase === 'LOBBY' || msg.phase === 'LEVEL_INTRO' || msg.phase === 'GAME_OVER') {
              next.superOffer = null;
            }
            return next;
          }
          case 'quiz.question':
            questionRef.current = msg;
            return { ...prev, question: msg, reveal: null, answered: null, status: 'open' };
          case 'quiz.answered':
            return { ...prev, answered: msg };
          case 'quiz.reveal':
            return { ...prev, reveal: msg };
          case 'quiz.level_stats':
            return { ...prev, levelStats: msg };
          case 'quiz.super_offer':
            return { ...prev, superOffer: msg };
          case 'quiz.game_over':
            return { ...prev, gameOver: msg };
          case 'session_replaced':
            playerIdRef.current = null;
            if (code) storePlayerId(code, null);
            return { ...prev, replaced: true, playerId: null, status: 'open' };
          case 'reconnect':
            playerIdRef.current = msg.playerId;
            if (code) storePlayerId(code, msg.playerId);
            return { ...prev, playerId: msg.playerId, replaced: false, error: null, status: 'open' };
          case 'error':
            if (msg.code === 'PLAYER_NOT_FOUND' && playerIdRef.current) {
              playerIdRef.current = null;
              if (code) storePlayerId(code, null);
              return { ...prev, playerId: null, error: msg, status: 'open' };
            }
            return { ...prev, error: msg };
          case 'pong':
            return prev;
          default:
            return prev;
        }
      });
    };

    connectRef.current = connect;
    const connectTimer = window.setTimeout(connect, 0);

    return () => {
      alive = false;
      closedByUs.current = true;
      connectRef.current = null;
      window.clearTimeout(connectTimer);
      if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [code]);

  const reconnect = useCallback(() => {
    if (closedByUs.current || !connectRef.current) return;
    if (reconnectTimerRef.current) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = undefined;
    }
    const ws = wsRef.current;
    if (ws) {
      ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
      try {
        ws.close();
      } catch {
        // Already closing/closed; a new socket replaces it below.
      }
    }
    wsRef.current = null;
    reconnectRef.current = 0;
    setState((s) => ({ ...s, status: 'connecting' }));
    connectRef.current();
  }, []);

  useConnectionWatchdog({ wsRef, stoppedRef: closedByUs, lastMessageAtRef, reconnect });

  const join = useCallback(
    (name: string, avatar: string) => send({ type: 'quiz.join', name, avatar }),
    [send],
  );

  const start = useCallback(() => send({ type: 'quiz.start' }), [send]);

  const answer = useCallback(
    (optionIndex: number) => {
      const q = questionRef.current;
      if (!q) return false;
      return send({
        type: 'quiz.answer',
        levelIndex: q.levelIndex,
        questionIndex: q.questionIndex,
        optionIndex,
      });
    },
    [send],
  );

  const advance = useCallback(() => send({ type: 'quiz.advance' }), [send]);

  const superChoice = useCallback(
    (accept: boolean) => send({ type: 'quiz.super_choice', accept }),
    [send],
  );

  const playAgain = useCallback(() => send({ type: 'quiz.play_again' }), [send]);

  const clearError = useCallback(() => setState((s) => ({ ...s, error: null })), []);
  const clearReplaced = useCallback(() => setState((s) => ({ ...s, replaced: false })), []);

  return useMemo(
    () => ({ state, join, start, answer, advance, superChoice, playAgain, clearError, clearReplaced, reconnect }),
    [state, join, start, answer, advance, superChoice, playAgain, clearError, clearReplaced, reconnect],
  );
}
